// Wallpaper value helpers (pure — no React, no I/O).
//
// NEM-1 FIX. The `bg_wallpaper` slot holds one of two kinds of string:
//   1. an IMAGE reference — an http(s) URL, or a `data:` URI (uploaded image,
//      and now a stock Animated-Loop SVG data URI), OR
//   2. a CSS GRADIENT value — `linear-gradient(...)` / `radial-gradient(...)` /
//      `conic-gradient(...)` (the curated gradient library, currentBg === 3),
//      applied directly as a CSS `background` string.
//
// The image-only controls (crop / remove thumbnail, "Image Tint") must show for
// case 1 and hide for case 2. The legacy test `bgWallpaper.includes("linear")`
// was a crude gradient sniff that FALSE-POSITIVES on the new loop data URIs:
// almost every animated loop's SVG contains the substring "linear" (the CSS
// timing keyword `... linear infinite` and/or `<linearGradient>`), so 17/20 loops
// were wrongly treated as gradients and lost their preview/crop/remove/tint UI.
//
// A CSS gradient VALUE always STARTS with `<type>-gradient(` — an image URL/data
// URI never does (it starts with `http`/`data:`). Anchoring the test at the start
// (like LinkWheel.jsx already does) is the correct, non-false-matching sniff, and
// covers all three gradient functions the gradient library now ships.
const GRADIENT_VALUE = /^(?:linear|radial|conic)-gradient\(/i;

/**
 * Is this wallpaper value a CSS gradient string (not an image)? Object values
 * (e.g. `{ src }`) and non-strings are never gradients — they are images.
 */
export const isGradientWallpaper = (value) =>
  typeof value === "string" && GRADIENT_VALUE.test(value.trim());

// The gradient-preset value written when the user clears the per-Segment
// gradient (the "none"/clear swatch). Kept here so the accordion and
// non-accordion picker branches use the SAME value.
export const CLEAR_GRADIENT_VALUE = "transparent";

/**
 * Does this gradient preset swatch represent the segment's CURRENTLY-selected
 * gradient? The selected gradient is stored in `segmentBgColor` (currentBg===3,
 * see useSegmentSetting `segmentBgGradientColor`). The picker previously compared
 * the swatch against the `background` prop — which is wired to
 * `wallpaperOverlayColor`, NOT the gradient — so the highlight NEVER matched a
 * gradient (F4.3). Compare against `segmentBgColor` so the active preset shows
 * its selection border.
 */
export const isGradientSwatchSelected = (segmentBgColor, color) =>
  typeof color === "string" && color !== "" && segmentBgColor === color;

/**
 * F6.11 FIX. `originalImage` is the un-cropped source the ImageCropper re-opens
 * on, so a user can re-crop from the full image instead of a previously-cropped
 * one. It must be (re)seeded whenever a croppable image `bgWallpaper` arrives by
 * NON-cropper means — most importantly when the persisted value hydrates AFTER
 * first mount (so `originalImage` started `null`), or when a different segment is
 * selected. The old effect guarded on `!setOriginalImage` — the state SETTER, a
 * function that is always truthy — so the branch was dead and `originalImage`
 * never refreshed, leaving re-crop-after-reload with a stale/absent original.
 *
 * Pure resolver so the seed decision is unit-testable without rendering React:
 *  - never clobbers an original already captured (protects an in-progress crop);
 *  - ignores gradients and empty values (they are not croppable images);
 *  - otherwise seeds from the fresh image reference (string URL/data-URI or
 *    `{ src }` object form).
 * Returns `currentOriginal` UNCHANGED when no seed is needed, so a functional
 * `setState(prev => resolveOriginalImage(bg, prev))` bails out (no re-render).
 *
 * @param {string|{src?:string}|null|undefined} bgWallpaper
 * @param {string|null|undefined} currentOriginal
 * @returns {string|null|undefined}
 */
export const resolveOriginalImage = (bgWallpaper, currentOriginal) => {
  if (currentOriginal) return currentOriginal;
  if (!bgWallpaper || isGradientWallpaper(bgWallpaper)) return currentOriginal;
  return bgWallpaper?.src || bgWallpaper;
};

/**
 * Is this Segment's background currently CLEARED (no per-Segment background)?
 *
 * Clearing is one canonical state, whichever tab clears it: the wallpaper value
 * becomes `CLEAR_GRADIENT_VALUE`, which the view layers already special-case as
 * "paint nothing" (SegmentView/styles.js + pages/Segment/styles.js both branch on
 * `segmentBgColor === "transparent"`). Used to show the clear tile as selected.
 */
export const isSegmentBackgroundCleared = (segmentBgColor) =>
  segmentBgColor === CLEAR_GRADIENT_VALUE;
